// Riser diagram dataset — schematic-style fire alarm risers with clickable annotations.
// Each diagram = floors[] (top→bottom) with devices, plus FACU + power + supervising station.
// Symbol "type" drives the icon/color in the SVG component.
//
// Device types: smoke, heat, pull, horn-strobe, strobe, speaker, module-input,
//               module-output, addressable-detector, duct-detector, sprinkler-flow,
//               sprinkler-tamper, FACU, NAC-booster, supervising-station, BDA, EVACS

export const RISER_DIAGRAMS = [
  {
    id: "small-commercial",
    name: "Small Commercial — 5-story office",
    short: "5-story Class B office, addressable SLC, NAC sync",
    summary:
      "Typical Class B occupancy: street-level retail, 4 floors office above, single-stair w/ areas of refuge. Single addressable FACU, two SLC loops, NAC sync per zone.",
    nicet: "NICET Level 1 / 2",
    citations: ["NFPA 72 §10.5", "NFPA 72 §17 (initiating)", "NFPA 72 §18 (notification)"],
    floors: [
      {
        label: "ROOF",
        devices: [
          { id: "f5-d1", type: "duct-detector", x: 30, label: "Duct-D AHU-1", lessonId: "m1-l1", note: "Duct smoke per NFPA 72 §17.7.5; HVAC > 2000 cfm." },
        ],
      },
      {
        label: "FL 4 — OFFICE",
        devices: [
          { id: "f4-s1", type: "smoke", x: 25, label: "S-401", lessonId: "m1-l1", note: "Photoelectric, addressable. 30 ft on-center per Table 17.6.3.1.1." },
          { id: "f4-s2", type: "smoke", x: 50, label: "S-402", lessonId: "m1-l1" },
          { id: "f4-h1", type: "horn-strobe", x: 75, label: "HS-401", lessonId: "m1-l2", note: "Wall ≤ 24 in below ceiling; sync per §18.5.5.6." },
        ],
      },
      {
        label: "FL 3 — OFFICE",
        devices: [
          { id: "f3-s1", type: "smoke", x: 25, label: "S-301", lessonId: "m1-l1" },
          { id: "f3-p1", type: "pull", x: 55, label: "P-301", lessonId: "m1-l2", note: "Within 5 ft of exit; 42-48 in AFF per ADA 308.2." },
          { id: "f3-h1", type: "horn-strobe", x: 75, label: "HS-301", lessonId: "m1-l2" },
        ],
      },
      {
        label: "FL 2 — OFFICE",
        devices: [
          { id: "f2-s1", type: "smoke", x: 25, label: "S-201", lessonId: "m1-l1" },
          { id: "f2-h1", type: "horn-strobe", x: 75, label: "HS-201", lessonId: "m1-l2" },
        ],
      },
      {
        label: "FL 1 — RETAIL",
        devices: [
          { id: "f1-p1", type: "pull", x: 15, label: "P-101", lessonId: "m1-l2", note: "Required at every exit per §17.14.8." },
          { id: "f1-h1", type: "heat", x: 35, label: "H-101", lessonId: "m1-l1", note: "135°F fixed. Used in sprinklered storage." },
          { id: "f1-hs1", type: "horn-strobe", x: 60, label: "HS-101", lessonId: "m1-l2" },
          { id: "f1-flow", type: "sprinkler-flow", x: 80, label: "FS-1", lessonId: "m1-l2", note: "Vane flow switch on riser; 30-60 sec retard." },
        ],
      },
      {
        label: "BSMT — MECH",
        devices: [
          { id: "b1-facu", type: "FACU", x: 25, label: "FACU", lessonId: "m1-l2", note: "Addressable panel, 2 SLC loops × 159 devices each." },
          { id: "b1-batt", type: "module-input", x: 50, label: "BAT 24V", lessonId: "m3-l1", note: "Sealed lead-acid; 24 hr standby + 5 min alarm." },
          { id: "b1-tamper", type: "sprinkler-tamper", x: 75, label: "TS-1", lessonId: "m1-l2", note: "Supervisory — main valve closure detection." },
        ],
      },
    ],
    offPremises: { type: "supervising-station", label: "Central Station (UL 827)", note: "Dual-path IP+cellular DACT per NFPA 72 §26.6." },
  },

  {
    id: "high-rise",
    name: "High-Rise — Class A SLC w/ EVACS",
    short: "20-story office, Class A pathway, EVACS, FCC, fire phones",
    summary:
      "High-rise (NFPA 72 §24 + IBC §403): EVACS w/ floor-by-floor selective paging, dedicated fire phones, FCC at grade. Class A SLC pathway for survivability. Networked FACUs.",
    nicet: "NICET Level 2 / 3",
    citations: ["NFPA 72 §24.4", "NFPA 72 §12.3 (Class A)", "IBC §403", "NFPA 72 §10.5"],
    floors: [
      {
        label: "ROOF",
        devices: [
          { id: "rf-bda", type: "BDA", x: 50, label: "BDA", lessonId: "m9-l2", note: "Bidirectional amplifier per UL 2524 + IBC §510." },
        ],
      },
      {
        label: "FL 20",
        devices: [
          { id: "f20-s1", type: "addressable-detector", x: 25, label: "AS-2001", lessonId: "m1-l3" },
          { id: "f20-spk", type: "speaker", x: 55, label: "SPK-20", lessonId: "m10-l1", note: "EVACS speaker; 70.7V or 25V; STI-PA ≥ 0.50." },
          { id: "f20-fp", type: "module-input", x: 80, label: "FP-20", lessonId: "m9-l2", note: "Two-way fire-fighter phone; UL 2017." },
        ],
      },
      {
        label: "FL 10",
        devices: [
          { id: "f10-s1", type: "addressable-detector", x: 25, label: "AS-1001", lessonId: "m1-l3" },
          { id: "f10-spk", type: "speaker", x: 55, label: "SPK-10", lessonId: "m10-l1" },
          { id: "f10-bst", type: "NAC-booster", x: 78, label: "PSE-10", lessonId: "m1-l2", note: "Power-supply expander; isolates voltage drop per floor." },
        ],
      },
      {
        label: "FL 5",
        devices: [
          { id: "f5-s1", type: "addressable-detector", x: 25, label: "AS-501", lessonId: "m1-l3" },
          { id: "f5-spk", type: "speaker", x: 55, label: "SPK-5", lessonId: "m10-l1" },
        ],
      },
      {
        label: "FL 1 — LOBBY/FCC",
        devices: [
          { id: "fcc", type: "FACU", x: 25, label: "FCC FACU", lessonId: "m11-l2", note: "Fire Command Center — IBC §911. Master FACU + EVACS controls." },
          { id: "fp-mic", type: "module-output", x: 55, label: "EVAC MIC", lessonId: "m10-l1", note: "Manual paging override; selectable by floor/zone." },
          { id: "fp-cmd", type: "module-output", x: 80, label: "FP CMD", lessonId: "m9-l2", note: "Fire-fighter phone master; supervises 50+ remote handsets." },
        ],
      },
      {
        label: "BSMT",
        devices: [
          { id: "bsmt-gen", type: "module-input", x: 25, label: "GEN", lessonId: "m3-l1", note: "Emergency generator status; ATS supervisory." },
          { id: "bsmt-batt", type: "module-input", x: 55, label: "BAT 24V", lessonId: "m3-l1", note: "Battery calc must meet 24 hr standby + 2 hr EVACS alarm load." },
          { id: "bsmt-flow", type: "sprinkler-flow", x: 80, label: "FS-Riser", lessonId: "m1-l2" },
        ],
      },
    ],
    offPremises: { type: "supervising-station", label: "Central Station + AHJ", note: "NFPA 72 §26.6 dual-path; AHJ also receives via radio in many jurisdictions." },
  },

  {
    id: "healthcare",
    name: "Healthcare — Defend-in-Place",
    short: "I-2 hospital w/ smoke compartments + door release + duct smoke",
    summary:
      "I-2 occupancy: defend-in-place. Magnetic door holders release on alarm (LSC §19), compartmentation per §18.3.7, duct smoke detection on all return air, MRI-rated detectors in suite.",
    nicet: "NICET Level 3",
    citations: ["NFPA 101 §19", "NFPA 72 §17.7.5", "NFPA 99", "NFPA 72 §21.7"],
    floors: [
      {
        label: "FL 4 — PATIENT",
        devices: [
          { id: "h4-s1", type: "addressable-detector", x: 22, label: "AS-401", lessonId: "m14-l1" },
          { id: "h4-mr", type: "smoke", x: 45, label: "S-MRI", lessonId: "m14-l1", note: "MRI-suite: non-ferrous, fiber-optic linkage. Special listed." },
          { id: "h4-dh", type: "module-output", x: 65, label: "DH-4", lessonId: "m14-l1", note: "Door-holder release per LSC §19.3.7.10." },
          { id: "h4-spk", type: "speaker", x: 85, label: "SPK-4", lessonId: "m10-l1", note: "Coded chime per §18.3.4.3.2 (PAS or coded)." },
        ],
      },
      {
        label: "FL 3 — OR/SURG",
        devices: [
          { id: "h3-s1", type: "addressable-detector", x: 22, label: "AS-301", lessonId: "m14-l1" },
          { id: "h3-d1", type: "duct-detector", x: 45, label: "DD-3", lessonId: "m1-l1", note: "Return-air duct smoke; shuts down AHU-3." },
          { id: "h3-dh", type: "module-output", x: 65, label: "DH-3", lessonId: "m14-l1" },
          { id: "h3-spk", type: "speaker", x: 85, label: "SPK-3", lessonId: "m10-l1" },
        ],
      },
      {
        label: "FL 2 — IMAGING",
        devices: [
          { id: "h2-s1", type: "addressable-detector", x: 22, label: "AS-201", lessonId: "m14-l1" },
          { id: "h2-d2", type: "duct-detector", x: 45, label: "DD-2", lessonId: "m1-l1" },
          { id: "h2-spk", type: "speaker", x: 85, label: "SPK-2", lessonId: "m10-l1" },
        ],
      },
      {
        label: "FL 1 — LOBBY/ADMIN",
        devices: [
          { id: "h1-facu", type: "FACU", x: 22, label: "FACU + ANN", lessonId: "m14-l1", note: "Networked FACU; remote graphic annunciator at each nurse station." },
          { id: "h1-pull", type: "pull", x: 50, label: "P-101", lessonId: "m1-l2", note: "Within 5 ft of exit; coded for staff response per LSC §19." },
          { id: "h1-flow", type: "sprinkler-flow", x: 80, label: "FS-1", lessonId: "m1-l2" },
        ],
      },
      {
        label: "BSMT",
        devices: [
          { id: "h-bsmt-gen", type: "module-input", x: 22, label: "GEN", lessonId: "m3-l1", note: "Type 10 / Class X / Level 1 EPSS per NFPA 110." },
          { id: "h-bsmt-bat", type: "module-input", x: 50, label: "BAT", lessonId: "m3-l1" },
          { id: "h-bsmt-tamp", type: "sprinkler-tamper", x: 80, label: "TS-1", lessonId: "m1-l2" },
        ],
      },
    ],
    offPremises: { type: "supervising-station", label: "Central Station + Nurse Stations", note: "Dual-path; remote annunciators at every nurse station per §18." },
  },

  {
    id: "mixed-use-residential",
    name: "Mixed-Use — Retail + R-2 Residential",
    short: "Ground-floor retail under 6-story R-2; partial coverage, in-unit smokes",
    summary:
      "R-2 residential above commercial: NFPA 72 §29 single-station smokes inside dwellings + system smokes in common areas. Sprinkler flow + manual pulls in commons. Carbon monoxide detection per IRC §R315.",
    nicet: "NICET Level 1 / 2",
    citations: ["NFPA 72 §29", "NFPA 72 §17.7", "IBC §907.2.9", "IRC §R315"],
    floors: [
      {
        label: "ROOF",
        devices: [
          { id: "mr-rf", type: "duct-detector", x: 50, label: "DD-RF", lessonId: "m1-l1" },
        ],
      },
      {
        label: "FL 6 — RES",
        devices: [
          { id: "m6-cs", type: "smoke", x: 25, label: "S-Corr-6", lessonId: "m1-l1", note: "Common-corridor smoke detection per §17.7.4." },
          { id: "m6-unit", type: "module-input", x: 55, label: "Unit-S 601", lessonId: "m1-l1", note: "In-unit single-station smoke (NFPA 72 §29) — power from unit branch." },
          { id: "m6-co", type: "module-input", x: 80, label: "CO-Det 6", lessonId: "m1-l1", note: "Carbon monoxide per UL 2034 + IRC §R315." },
        ],
      },
      {
        label: "FL 4 — RES",
        devices: [
          { id: "m4-cs", type: "smoke", x: 25, label: "S-Corr-4", lessonId: "m1-l1" },
          { id: "m4-hs", type: "horn-strobe", x: 55, label: "HS-Corr-4", lessonId: "m1-l2" },
          { id: "m4-co", type: "module-input", x: 80, label: "CO-Det 4", lessonId: "m1-l1" },
        ],
      },
      {
        label: "FL 2 — RES",
        devices: [
          { id: "m2-cs", type: "smoke", x: 25, label: "S-Corr-2", lessonId: "m1-l1" },
          { id: "m2-hs", type: "horn-strobe", x: 55, label: "HS-Corr-2", lessonId: "m1-l2" },
        ],
      },
      {
        label: "FL 1 — RETAIL",
        devices: [
          { id: "m1-pull", type: "pull", x: 15, label: "P-101", lessonId: "m1-l2" },
          { id: "m1-s1", type: "smoke", x: 35, label: "S-101", lessonId: "m1-l1" },
          { id: "m1-hs", type: "horn-strobe", x: 60, label: "HS-101", lessonId: "m1-l2" },
          { id: "m1-flow", type: "sprinkler-flow", x: 82, label: "FS-1", lessonId: "m1-l2" },
        ],
      },
      {
        label: "BSMT",
        devices: [
          { id: "mr-facu", type: "FACU", x: 22, label: "FACU", lessonId: "m1-l2", note: "Addressable panel; covers commons + sprinkler. Apartments are 29-listed (own PSU)." },
          { id: "mr-bat", type: "module-input", x: 55, label: "BAT 24V", lessonId: "m3-l1" },
          { id: "mr-tamp", type: "sprinkler-tamper", x: 82, label: "TS-1", lessonId: "m1-l2" },
        ],
      },
    ],
    offPremises: { type: "supervising-station", label: "Central Station (UL 827)", note: "Single transmission of any common-area alarm initiates dispatch." },
  },
];

export const SYMBOL_LEGEND = [
  { type: "FACU", label: "FACU / Control Panel" },
  { type: "smoke", label: "Smoke Detector (photo/ion)" },
  { type: "addressable-detector", label: "Addressable Smoke" },
  { type: "duct-detector", label: "Duct Smoke Detector" },
  { type: "heat", label: "Heat Detector" },
  { type: "pull", label: "Manual Pull Station" },
  { type: "horn-strobe", label: "Horn / Strobe" },
  { type: "strobe", label: "Strobe Only" },
  { type: "speaker", label: "Voice Speaker (EVACS)" },
  { type: "sprinkler-flow", label: "Waterflow Switch" },
  { type: "sprinkler-tamper", label: "Tamper Switch (Supervisory)" },
  { type: "NAC-booster", label: "NAC Booster / PSE" },
  { type: "module-input", label: "Monitor Module / Input" },
  { type: "module-output", label: "Relay / Output Module" },
  { type: "BDA", label: "Bidirectional Amplifier" },
  { type: "supervising-station", label: "Central / Supervising Station" },
];
